import type { DefaultPaginatedFilters } from "@/api/shared/filters";
import { createService, getAllPathParam, getPaginatedFilteredPathParam } from "@/api/shared";
import type { NoPathParams } from "@/api/shared";
import type { TPhone } from "@/api/shared/contacts.ts";
import type { TPatient } from "@/api/models/patient/patient";
import type { TJobsAvailableForUser } from "@/api/models/patient/patient";
import type { EActiveTreatmentType } from "@/api/models/patient/dashboard";
import type { ECooperatePartiesType, ETreatmentPlanJobState } from "@/api/models/treatmentPlan/job";
import type { ETreatmentType } from "@/api/models/treatmentPlan/treatmentPlan";
import type { TCooperatePracticeLink, TCoopearateStaffLink } from "../treatmentPlan/types";

const SLUG = "treatment_plan";

export interface ICotreatmentDashboardFilters extends DefaultPaginatedFilters {
  /** Drives the dashboard tabs: `true` → Actions Needed, `false` → In Progress, omitted → In Treatment. */
  has_actionable_jobs?: boolean;
  /** Comma-separated GP practice ids (serialized from an array by `filtersToQueryString`). */
  assigned_gp_practice?: string[];
  /** Comma-separated ortho practice ids. */
  assigned_ortho_practice?: string[];
  /** Comma-separated GP staff ids. */
  assigned_gp?: string[];
  /** Comma-separated ortho staff ids. */
  assigned_ortho?: string[];
  last_update__gte?: string;
  last_update__lte?: string;
  ordering?: "last_name" | "-last_name" | "last_update" | "-last_update";
  paginate?: boolean;
  state?: ETreatmentPlanJobState[];
}

/**
 * A row of `cooperate-practices/` — one distinct practice per link type.
 *
 * The backend serializes this with the same `CooperatePracticeLinkSerializer` it
 * uses for dashboard rows, so the practice (including its `addresses`) is nested
 * under `practice` rather than flattened onto the row.
 */
export type TCooperatePracticeOption = TCooperatePracticeLink;

export interface ICooperatePracticeOptionFilters extends DefaultPaginatedFilters {
  /** Comma-separated link types; omitted returns all. */
  type?: ECooperatePartiesType[];
}

/**
 * The `patient` sub-object embedded in a cotreatment dashboard row. Mirrors the
 * fields of `TPatientDashboardItem` — the all-patients dashboard returns these
 * flat on the row, here they are nested under `patient`.
 */
export type TCotreatmentDashboardPatient = Pick<
  TPatient,
  | "id"
  | "is_patient"
  | "first_name"
  | "last_name"
  | "middle_name"
  | "sex"
  | "language"
  | "picture"
  | "picture_default"
  | "hrid"
  | "tags"
> & {
  phone_contact: TPhone | null;
  active_treatment_types: EActiveTreatmentType[];
};

export type TCotreatmentDashboardItem = {
  id: string;
  state: ETreatmentPlanJobState;
  treatment_type: ETreatmentType;
  last_update: string;
  jobs_available_for_user: TJobsAvailableForUser[];
  patient: TCotreatmentDashboardPatient;
  /** Read-only on the backend. */
  cooperate_staff: TCoopearateStaffLink[];
  /** Read-only on the backend. */
  cooperate_practice: TCooperatePracticeLink[];
};

export default createService(SLUG, {
  dashboard: getPaginatedFilteredPathParam<
    TCotreatmentDashboardItem,
    NoPathParams,
    ICotreatmentDashboardFilters
  >(`${SLUG}/cotreatment-dashboard/`, `${SLUG}/cotreatment-dashboard/search/`),

  /** Option list for the Assigned GP/Ortho Office filters. Returns a flat, unpaginated array. */
  cooperatePractices: getAllPathParam<
    TCooperatePracticeOption,
    NoPathParams,
    ICooperatePracticeOptionFilters
  >(`${SLUG}/cotreatment-dashboard/cooperate-practices`),
});
